/**
 * Keyword Research Service
 * 
 * Servicio para consultar el Google Keyword Planner API
 * a través de Cloud Functions
 * 
 * @example
 * const result = await keywordResearchService.generateKeywordIdeas({
 *   seedKeywords: ['psicólogo madrid', 'terapia ansiedad'],
 *   geoTarget: 'spain'
 * });
 */

import { functions } from '@/core/config/firebase';

// ============================================
// TYPES
// ============================================

export interface MonthlySearchVolume {
    year: number;
    month: number;
    monthlySearches: number;
}

export interface KeywordIdea {
    keyword: string;
    avgMonthlySearches: number;
    competition: 'LOW' | 'MEDIUM' | 'HIGH' | 'UNSPECIFIED';
    competitionIndex: number;
    lowTopOfPageBidMicros?: number;
    highTopOfPageBidMicros?: number;
    avgCpcEuros?: number;
    monthlySearchVolumes?: MonthlySearchVolume[];
}

export interface KeywordResearchRequest {
    /** Seed keywords to generate ideas from (max 10) */
    seedKeywords: string[];
    /** Target region */
    geoTarget?: 'spain' | 'mexico' | 'argentina' | 'colombia' | 'chile' | 'usa' | 'latam';
}

export interface KeywordResearchResponse {
    keywords: KeywordIdea[];
    totalResults: number;
    requestedAt: string;
}

export interface KeywordResearchHistoryItem {
    id: string;
    seedKeywords: string[];
    resultsCount: number;
    topKeywords: Array<{
        keyword: string;
        searches: number;
        competition?: string;
    }>;
    createdAt: any;
}

// ============================================
// CLOUD FUNCTION REFERENCES
// ============================================

const generateKeywordIdeasFn = functions.httpsCallable('generateKeywordIdeas', {
    timeout: 60000
});

const getKeywordResearchHistoryFn = functions.httpsCallable('getKeywordResearchHistory', {
    timeout: 30000
});

// ============================================
// SERVICE
// ============================================

export const keywordResearchService = {
    /**
     * Generate keyword ideas from seed keywords
     * Uses Google Keyword Planner API to get real search volumes
     */
    async generateKeywordIdeas(request: KeywordResearchRequest): Promise<KeywordResearchResponse> {
        const result = await generateKeywordIdeasFn({
            seedKeywords: request.seedKeywords,
            geoTarget: request.geoTarget || 'spain'
        });
        return result.data as KeywordResearchResponse;
    },

    /**
     * Get recent keyword research history
     */
    async getHistory(limit: number = 10): Promise<KeywordResearchHistoryItem[]> {
        const result = await getKeywordResearchHistoryFn({ limit });
        return result.data as KeywordResearchHistoryItem[];
    },

    /**
     * Helper: Format search volume for display
     */
    formatVolume(volume: number): string {
        if (volume >= 1000000) {
            return `${(volume / 1000000).toFixed(1)}M`;
        }
        if (volume >= 1000) {
            return `${(volume / 1000).toFixed(1)}K`;
        }
        return volume.toString();
    },

    /**
     * Helper: Get competition color class
     */
    getCompetitionColor(competition: string): string {
        switch (competition) {
            case 'LOW':
                return 'text-green-600 bg-green-100';
            case 'MEDIUM':
                return 'text-yellow-600 bg-yellow-100';
            case 'HIGH':
                return 'text-red-600 bg-red-100';
            default:
                return 'text-gray-600 bg-gray-100';
        }
    },

    /**
     * Helper: Get competition label in Spanish
     */
    getCompetitionLabel(competition: string): string {
        switch (competition) {
            case 'LOW':
                return 'Baja';
            case 'MEDIUM':
                return 'Media';
            case 'HIGH':
                return 'Alta';
            default:
                return 'N/A';
        }
    }
};

export default keywordResearchService;
